import React, { useState } from 'react';
import { 
  ArrowLeft, Save, X, Plus, Trash2,
  PackageCheck, Microscope, ClipboardCheck, Truck, CalendarCheck
} from 'lucide-react';
import { inspectionTypes, inspectionPlans, inspectionMethods } from '../../data/qualityControlData';
import { products } from '../../data/inventoryData';

const InspectionCreate = ({ onBack, onSave }) => {
  const [formData, setFormData] = useState({
    inspection_type: 'incoming',
    product_id: '',
    plan_id: '',
    quantity_inspected: '',
    inspector: '',
    inspection_date: new Date().toISOString().split('T')[0],
    notes: '',
    checkpoints: []
  });
  
  const [errors, setErrors] = useState({});
  
  // Get inspection type icon
  const getInspectionTypeIcon = (type) => {
    const inspectionType = inspectionTypes.find(t => t.id === type);
    switch(type) {
      case 'incoming':
        return <PackageCheck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'in-process':
        return <Microscope className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'final':
        return <ClipboardCheck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'outgoing':
        return <Truck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'periodic':
        return <CalendarCheck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      default:
        return <ClipboardCheck className="h-5 w-5 text-blue-500" />;
    }
  };
  
  // Filter plans by inspection type
  const filteredPlans = inspectionPlans.filter(plan => plan.inspection_type === formData.inspection_type);
  
  // Get selected plan
  const selectedPlan = inspectionPlans.find(plan => plan.id === formData.plan_id);
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Special handling for plan_id change
    if (name === 'plan_id') {
      const plan = inspectionPlans.find(p => p.id === value);
      if (plan) {
        // Initialize checkpoints from plan
        const checkpoints = plan.checkpoints.map(cp => ({
          checkpoint_id: cp.id,
          result: 'pending',
          value: '',
          notes: ''
        }));
        
        setFormData(prev => ({
          ...prev,
          checkpoints
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          checkpoints: []
        }));
      }
    }
    
    // Special handling for inspection_type change
    if (name === 'inspection_type') {
      setFormData(prev => ({
        ...prev,
        plan_id: '',
        checkpoints: []
      }));
    }
  };
  
  // Handle checkpoint change
  const handleCheckpointChange = (index, field, value) => {
    const updatedCheckpoints = [...formData.checkpoints];
    updatedCheckpoints[index] = {
      ...updatedCheckpoints[index],
      [field]: value
    };
    
    setFormData(prev => ({
      ...prev,
      checkpoints: updatedCheckpoints
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.product_id) newErrors.product_id = '請選擇產品';
    if (!formData.plan_id) newErrors.plan_id = '請選擇檢驗計劃';
    if (!formData.quantity_inspected || formData.quantity_inspected <= 0) {
      newErrors.quantity_inspected = '請輸入有效的檢驗數量';
    }
    if (!formData.inspector) newErrors.inspector = '請輸入檢驗人員';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real app, we would submit the data to the server here
      console.log('Form data submitted:', formData);
      onSave();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">新增檢驗記錄</h1>
        </div>
        <button
          onClick={handleSubmit}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Save className="h-4 w-4 mr-2" />
          儲存
        </button>
      </div>
      
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-medium">基本資訊</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Inspection Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">檢驗類型</label>
                <div className="flex space-x-2">
                  {inspectionTypes.map(type => {
                    const isSelected = formData.inspection_type === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        className={`flex items-center px-3 py-2 rounded-lg border ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}
                        onClick={() => handleInputChange({ target: { name: 'inspection_type', value: type.id } })}
                      >
                        <div className="p-1 rounded-md mr-2" style={{ backgroundColor: `${type.color}20` }}>
                          {getInspectionTypeIcon(type.id)}
                        </div>
                        <span className={isSelected ? 'text-blue-700' : 'text-gray-700'}>{type.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Product */}
              <div>
                <label htmlFor="product_id" className="block text-sm font-medium text-gray-700 mb-1">產品</label>
                <select
                  id="product_id"
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleInputChange}
                  className={`block w-full rounded-md border ${errors.product_id ? 'border-red-300' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">選擇產品</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>{product.name} ({product.sku})</option>
                  ))}
                </select>
                {errors.product_id && <p className="mt-1 text-sm text-red-600">{errors.product_id}</p>}
              </div>
              
              {/* Inspection Plan */}
              <div>
                <label htmlFor="plan_id" className="block text-sm font-medium text-gray-700 mb-1">檢驗計劃</label>
                <select
                  id="plan_id"
                  name="plan_id"
                  value={formData.plan_id}
                  onChange={handleInputChange}
                  className={`block w-full rounded-md border ${errors.plan_id ? 'border-red-300' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">選擇檢驗計劃</option>
                  {filteredPlans.map(plan => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </select>
                {errors.plan_id && <p className="mt-1 text-sm text-red-600">{errors.plan_id}</p>}
              </div>
              
              {/* Quantity */}
              <div>
                <label htmlFor="quantity_inspected" className="block text-sm font-medium text-gray-700 mb-1">檢驗數量</label>
                <input
                  type="number"
                  id="quantity_inspected"
                  name="quantity_inspected"
                  value={formData.quantity_inspected}
                  onChange={handleInputChange}
                  min="1"
                  className={`block w-full rounded-md border ${errors.quantity_inspected ? 'border-red-300' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                />
                {errors.quantity_inspected && <p className="mt-1 text-sm text-red-600">{errors.quantity_inspected}</p>}
              </div>
              
              {/* Inspector */}
              <div>
                <label htmlFor="inspector" className="block text-sm font-medium text-gray-700 mb-1">檢驗人員</label>
                <input
                  type="text"
                  id="inspector"
                  name="inspector"
                  value={formData.inspector}
                  onChange={handleInputChange}
                  className={`block w-full rounded-md border ${errors.inspector ? 'border-red-300' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                />
                {errors.inspector && <p className="mt-1 text-sm text-red-600">{errors.inspector}</p>}
              </div>
              
              {/* Inspection Date */}
              <div>
                <label htmlFor="inspection_date" className="block text-sm font-medium text-gray-700 mb-1">檢驗日期</label>
                <input
                  type="date"
                  id="inspection_date"
                  name="inspection_date"
                  value={formData.inspection_date}
                  onChange={handleInputChange}
                  className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            {/* Notes */}
            <div className="mt-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">備註</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        
        {/* Checkpoints Card */}
        {selectedPlan && formData.checkpoints.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">檢驗項目</h2>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {formData.checkpoints.map((checkpoint, index) => {
                  const checkpointInfo = selectedPlan.checkpoints.find(cp => cp.id === checkpoint.checkpoint_id);
                  
                  return (
                    <div key={checkpoint.checkpoint_id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start">
                        <h3 className="text-base font-medium">{checkpointInfo?.name}</h3>
                      </div>
                      
                      <div className="mt-3 text-sm text-gray-500">
                        <p><span className="font-medium">檢驗方法:</span> {checkpointInfo?.method}</p>
                        <p className="mt-1"><span className="font-medium">檢驗標準:</span> {checkpointInfo?.criteria}</p>
                      </div>
                      
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Result */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">檢驗結果</label>
                          <select
                            value={checkpoint.result}
                            onChange={(e) => handleCheckpointChange(index, 'result', e.target.value)}
                            className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="pending">待定</option>
                            <option value="passed">合格</option>
                            <option value="failed">不合格</option>
                            <option value="conditional">條件合格</option>
                          </select>
                        </div>
                        
                        {/* Value */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">測量值</label>
                          <input
                            type="text"
                            value={checkpoint.value}
                            onChange={(e) => handleCheckpointChange(index, 'value', e.target.value)}
                            className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="輸入測量值或觀察結果"
                          />
                        </div>
                      </div>
                      
                      {/* Notes */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">備註</label>
                        <textarea
                          value={checkpoint.notes}
                          onChange={(e) => handleCheckpointChange(index, 'notes', e.target.value)}
                          rows="2"
                          className="block w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="輸入備註"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            儲存檢驗記錄
          </button>
        </div>
      </form>
    </div>
  );
};

export default InspectionCreate;
